import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Flame, Rocket } from "lucide-react";
import { TrialEngine } from "@/game/trials";
import { isCorrect } from "@/game/types";
import { TURN_DURATION_MS } from "@/theme";
import { SegmentTimer } from "@/components/game/SegmentTimer";
import { useAutoFocus } from "@/hooks/useAutoFocus";
import {
  createTurn,
  registerTap,
  expire,
  resolve,
  progressAt,
} from "@/play/turnMachine";
import { incrementGamesPlayed, updateBestScore, getAccessibility } from "@/play/storage";
import { hasProfile, openSession, submitScore } from "@/play/leaderboard";
import { speak, stopSpeaking, speechAvailable } from "@/play/speech";
import { requestWakeLock, releaseWakeLock } from "@/play/wakeLock";
import { vibrateTap } from "@/play/haptics";
import { playTick, playGo, primeSound, playTap, playCorrect, playWrong, playMilestone } from "@/play/sound";

// Grazia per catturare un pointerdown iniziato al bordo della scadenza prima di
// congelare il conteggio; e durata del lampo di esito.
const GRACE_MS = 120;
const FLASH_MS = 750;

// Traguardi dello streak → tier di celebrazione (1 piccolo, 2 medio, 3 celebrazione).
const MILESTONE_TIER = { 5: 1, 10: 2, 20: 3, 30: 3, 50: 3 };

/**
 * Game — loop di gioco (Fase 4).
 * Timer 10s (TURN_DURATION_MS, dal golden master) con requestAnimationFrame su
 * timestamp; conteggio tocchi su pointerdown (contatto), verifica a scadenza con
 * isCorrect del motore. src/game/* NON modificato.
 */
export default function Game() {
  const navigate = useNavigate();

  const engineRef = useRef(null);
  if (!engineRef.current) engineRef.current = new TrialEngine();
  const turnRef = useRef(null);
  const currentTrialRef = useRef(null);
  const rafRef = useRef(0);
  const graceRef = useRef(0);
  const flashRef = useRef(0);
  const speechStartRef = useRef(0);
  const lastPointerTsRef = useRef(-1e9);
  const streakRef = useRef(0);
  const accRef = useRef(false);
  const viewRef = useRef("countdown");
  const mountedRef = useRef(true);
  const countdownRef = useRef(0);
  const rippleIdRef = useRef(0);

  const headingRef = useAutoFocus();
  const overHeadingRef = useRef(null);

  const [trial, setTrial] = useState(null);
  const [view, setView] = useState("countdown"); // countdown | playing | result | over
  const [progress, setProgress] = useState(1);
  const [resultCorrect, setResultCorrect] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [announce, setAnnounce] = useState("");
  const [countLabel, setCountLabel] = useState("5");
  const [score, setScore] = useState(0);
  const [bestInfo, setBestInfo] = useState({ best: 0, isNewRecord: false });
  const [ripples, setRipples] = useState([]);
  const [milestone, setMilestone] = useState(null);
  const milestoneTimerRef = useRef(0);
  const sessionTokenRef = useRef(null);
  const submittedRef = useRef(false);
  const [serverBest, setServerBest] = useState(null); // {bestScore, isNewRecord}
  const [noProfile, setNoProfile] = useState(false);
  const [notRecorded, setNotRecorded] = useState(false);
  const [topFeedback, setTopFeedback] = useState(null); // {type:'entered'|'up',...}

  // Classifica online: apre una sessione lato server all'avvio di ogni partita
  // (solo se esiste un profilo). Il fallimento non blocca il gioco.
  const startSession = () => {
    sessionTokenRef.current = null;
    submittedRef.current = false;
    setServerBest(null);
    setNoProfile(false);
    setNotRecorded(false);
    setTopFeedback(null);
    if (!hasProfile()) return;
    openSession()
      .then((tok) => {
        sessionTokenRef.current = tok;
      })
      .catch(() => {
        sessionTokenRef.current = null;
      });
  };

  // Invio del punteggio finale (una sola volta per partita). Il bestScore
  // ufficiale mostrato è quello restituito dal server.
  const submitToLeaderboard = (finalScore) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    if (!hasProfile()) {
      setNoProfile(true);
      return;
    }
    const token = sessionTokenRef.current;
    if (!token) {
      setNotRecorded(true); // sessione non aperta (offline/errore all'avvio)
      return;
    }
    submitScore(token, finalScore)
      .then((res) => {
        if (!mountedRef.current || !res) return;
        setServerBest(res);
        // Feedback Top 10: solo se il punteggio fa ENTRARE o SALIRE nella top 10.
        const rank = res.rank;
        if (res.isNewRecord && rank && rank <= 10) {
          const old = res.oldRank;
          if (old == null) {
            // Nessun record precedente in classifica -> ingresso in Top 10.
            setTopFeedback({ type: "entered", rank, score: res.score });
          } else if (rank < old) {
            // Aveva già una posizione e la migliora salendo in Top 10.
            setTopFeedback({ type: "up", from: old, to: rank });
          }
        }
      })
      .catch(() => {
        if (mountedRef.current) setNotRecorded(true);
      });
  };

  const clearTimers = () => {
    cancelAnimationFrame(rafRef.current);
    clearTimeout(graceRef.current);
    clearTimeout(flashRef.current);
    clearTimeout(speechStartRef.current);
    clearTimeout(countdownRef.current);
    clearTimeout(milestoneTimerRef.current);
  };

  const doResolve = (t) => {
    const turn = turnRef.current;
    if (!turn) return;
    const finalTaps = resolve(turn); // congela conteggio, blocca nuovi tocchi
    const correct = isCorrect(t, finalTaps); // verifica del golden master
    if (correct) playCorrect(); else playWrong(); // SFX esito (si affianca al lampo)
    setResultCorrect(correct);
    setLastResult({
      correct,
      taps: finalTaps,
      required: t.requiredTaps,
      speech: t.speech,
      explanation: t.explanation,
    });
    const outcome = correct
      ? `Corretta. La risposta è ${t.requiredTaps}.`
      : `Sbagliata. Hai toccato ${finalTaps}, la risposta era ${t.requiredTaps}.${
          t.explanation ? " Perché: " + t.explanation : ""
        }`;
    setView("result");

    const advance = () => {
      if (!mountedRef.current) return;
      if (correct) {
        streakRef.current += 1;
        const s = streakRef.current;
        setScore(s);
        // Streak Milestone: micro-celebrazione (animazione pill + SFX dedicato) sui
        // traguardi. Non blocca nulla, si auto-pulisce, non tocca il gameplay.
        const tier = MILESTONE_TIER[s];
        if (tier) {
          playMilestone(tier);
          setMilestone({ tier, key: s });
          clearTimeout(milestoneTimerRef.current);
          milestoneTimerRef.current = setTimeout(() => {
            if (mountedRef.current) setMilestone(null);
          }, tier === 3 ? 1000 : 750);
        }
        startTurn();
      } else {
        // Fine partita: persistenza record + partite giocate (localStorage).
        const finalScore = streakRef.current;
        incrementGamesPlayed();
        setBestInfo(updateBestScore(finalScore));
        submitToLeaderboard(finalScore);
        releaseWakeLock();
        setView("over");
      }
    };

    if (accRef.current && speechAvailable()) {
      // Voce ON: annuncia l'esito COMPLETO e avanza SOLO quando la voce ha finito
      // (con fallback di sicurezza), così non viene troncato dalla domanda
      // successiva. Niente aria-live: eviterebbe il doppio annuncio.
      setAnnounce("");
      let done = false;
      const go = () => {
        if (done || !mountedRef.current) return;
        done = true;
        clearTimeout(flashRef.current);
        advance();
      };
      speak(outcome, go); // go() su onend/onerror (o subito se la voce è assente)
      flashRef.current = setTimeout(go, 8000); // fallback se onend non scatta (iOS)
    } else {
      // Voce OFF: aria-live per VoiceOver + breve lampo visivo.
      setAnnounce(outcome);
      flashRef.current = setTimeout(advance, FLASH_MS);
    }
  };

  const startTicking = (t) => {
    cancelAnimationFrame(rafRef.current);
    const tick = () => {
      const turn = turnRef.current;
      if (!turn) return;
      const t2 = performance.now();
      setProgress(progressAt(turn, t2));
      if (t2 >= turn.expiryTime) {
        expire(turn);
        graceRef.current = setTimeout(() => doResolve(t), GRACE_MS);
        return; // ferma la RAF: la scadenza è gestita dalla grazia + resolve
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  const startTurn = () => {
    const t = engineRef.current.next(streakRef.current);
    currentTrialRef.current = t;
    setTrial(t);
    setView("playing");
    setProgress(1);
    setAnnounce("");

    // Avvio effettivo del turno di 6s (createTurn + tick). Logica invariata.
    const beginTimer = () => {
      if (!mountedRef.current) return;
      const now = performance.now();
      turnRef.current = createTurn(TURN_DURATION_MS, now);
      startTicking(t);
    };

    if (accRef.current && speechAvailable()) {
      // ACCESSIBILITÀ ON: la domanda viene letta per intero e il timer di 6s parte
      // SOLO alla fine reale della lettura (evento onend/onerror del TTS), così il
      // tempo di lettura non consuma i 6 secondi. Nessun delay fisso stimato.
      // Cintura di sicurezza: se onend non scatta (Safari/iOS), avvia comunque.
      let started = false;
      const start = () => {
        if (started) return;
        started = true;
        clearTimeout(speechStartRef.current);
        // Non avviare solo se la partita è finita (endGame imposta viewRef="over").
        // NB: la lettura può concludersi prima che la view sia committata a
        // "playing" (TTS istantaneo); in tal caso il timer parte comunque (corretto).
        if (!mountedRef.current || viewRef.current === "over") return;
        beginTimer();
      };
      speak(t.speech, start); // start() su onend/onerror reale del TTS
      speechStartRef.current = setTimeout(start, 8000);
    } else {
      // Modalità normale (ACC OFF) o ACC ON senza TTS: comportamento invariato,
      // timer immediato.
      stopSpeaking();
      beginTimer();
    }
  };

  // Interruzione = SCONFITTA immediata (idempotente). Usata quando l'app perde
  // visibilità / va in background / schermo bloccato. Nessuna ripresa possibile,
  // nessun tempo extra: si chiude la partita con lo streak raggiunto finora.
  const endGame = () => {
    if (viewRef.current !== "playing") return; // solo durante il turno attivo
    viewRef.current = "over"; // idempotenza immediata contro eventi multipli
    clearTimers();
    stopSpeaking();
    if (turnRef.current) resolve(turnRef.current); // blocca ulteriori tocchi
    const finalScore = streakRef.current;
    incrementGamesPlayed();
    setBestInfo(updateBestScore(finalScore));
    submitToLeaderboard(finalScore);
    releaseWakeLock();
    setView("over");
  };

  // Countdown di preparazione (5→4→3→2→1→VIA!) prima di OGNI partita, uguale con
  // Accessibilità ON e OFF. Nessuna voce: solo tick sonori discreti (Web Audio) e
  // il numero a schermo. Dà a chi usa VoiceOver 5 secondi per disattivarlo.
  const runCountdown = () => {
    setView("countdown");
    viewRef.current = "countdown";
    const numbers = ["5", "4", "3", "2", "1"];
    let idx = 0;
    const step = () => {
      if (!mountedRef.current) return;
      if (idx < numbers.length) {
        setCountLabel(numbers[idx]);
        playTick();
        idx += 1;
        countdownRef.current = setTimeout(step, 1000);
      } else {
        setCountLabel("VIA!");
        playGo();
        countdownRef.current = setTimeout(() => {
          if (mountedRef.current) startTurn();
        }, 600);
      }
    };
    step();
  };

  // Avvio partita al montaggio.
  useEffect(() => {
    mountedRef.current = true;
    accRef.current = getAccessibility();
    engineRef.current.setAccessible(accRef.current);
    streakRef.current = 0;
    setScore(0);
    requestWakeLock();
    startSession();
    runCountdown();
    return () => {
      mountedRef.current = false;
      clearTimers();
      stopSpeaking();
      releaseWakeLock();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mantiene viewRef sincronizzato per i listener asincroni.
  useEffect(() => {
    viewRef.current = view;
  }, [view]);

  // Interruzioni del ciclo di vita = partita persa (equità, no tempo extra):
  //  - visibilitychange -> hidden (cambio app, schermo bloccato, altra app, tab);
  //  - pagehide (bfcache, chiusura, alcuni backgrounding);
  //  - pageshow persisted (ritorno da bfcache): impedisce qualsiasi ripresa.
  // NB: `performance.now()` resta il clock autorevole; se un evento sfuggisse, al
  // ritorno la RAF riprende e il tick rileva comunque la scadenza (cintura di sicurezza).
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "hidden") {
        endGame();
      } else if (document.visibilityState === "visible") {
        if (viewRef.current === "playing") requestWakeLock();
      }
    };
    const onPageHide = () => endGame();
    const onPageShow = (e) => {
      if (e.persisted) endGame();
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onPageShow);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Focus sulla schermata di fine partita.
  useEffect(() => {
    if (view === "over" && overHeadingRef.current) {
      overHeadingRef.current.focus({ preventScroll: true });
    }
  }, [view]);

  const tapAt = (tapTime) => {
    const turn = turnRef.current;
    if (!turn) return false;
    return registerTap(turn, tapTime);
  };

  // Onda breve nel punto esatto del tocco (feedback "tap registrato"). Tap ravvicinati
  // generano onde indipendenti; ognuna si auto-rimuove a fine animazione.
  const spawnRipple = (x, y) => {
    const id = ++rippleIdRef.current;
    setRipples((prev) => [...prev, { id, x, y }]);
    setTimeout(() => {
      if (!mountedRef.current) return;
      setRipples((prev) => prev.filter((r) => r.id !== id));
    }, 480);
  };

  // Tocco/click/tastiera sulla mega-superficie.
  const onPointerDown = (e) => {
    let tt = e.timeStamp;
    if (!Number.isFinite(tt) || tt <= 0 || tt > 1e12) tt = performance.now();
    lastPointerTsRef.current = performance.now();
    if (tapAt(tt)) {
      vibrateTap(); // feedback solo su tocco valido (no duplicati)
      playTap();
      const rect = e.currentTarget.getBoundingClientRect();
      spawnRipple(e.clientX - rect.left, e.clientY - rect.top);
    }
  };
  const onSurfaceClick = () => {
    // Percorso VoiceOver/tastiera: conta solo se NON c'è stato un pointerdown
    // recente (evita il doppio conteggio del click sintetico dopo il touch).
    if (performance.now() - lastPointerTsRef.current < 1000) return;
    if (tapAt(performance.now())) {
      vibrateTap();
      playTap();
    }
  };
  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (tapAt(performance.now())) {
        vibrateTap();
        playTap();
      }
    }
  };

  const restart = () => {
    clearTimers();
    engineRef.current.reset();
    accRef.current = getAccessibility();
    engineRef.current.setAccessible(accRef.current);
    streakRef.current = 0;
    setScore(0);
    requestWakeLock();
    primeSound();
    startSession();
    runCountdown();
  };

  if (view === "over") {
    return (
      <main className="gameover" data-testid="gameover-screen">
        {topFeedback && (
          <div
            className={`gameover__top10 gameover__top10--${topFeedback.type}`}
            data-testid="gameover-top10"
            role="status"
            aria-live="polite"
          >
            {topFeedback.type === "entered" ? (
              <>
                <span className="gameover__top10-head" aria-hidden="true">
                  <Flame size={18} strokeWidth={2.5} /> SEI ENTRATO IN TOP 10!
                </span>
                <span className="gameover__top10-sub" aria-hidden="true">
                  {topFeedback.rank}° posto — {topFeedback.score} punti
                </span>
                <span className="sr-only">
                  Sei entrato nella top 10. Posizione {topFeedback.rank}, {topFeedback.score} punti.
                </span>
              </>
            ) : (
              <>
                <span className="gameover__top10-head" aria-hidden="true">
                  <Rocket size={18} strokeWidth={2.5} /> SEI SALITO IN CLASSIFICA!
                </span>
                <span className="gameover__top10-sub" aria-hidden="true">
                  Dal {topFeedback.from}° al {topFeedback.to}° posto
                </span>
                <span className="sr-only">
                  Sei salito in classifica. Dalla posizione {topFeedback.from} alla posizione{" "}
                  {topFeedback.to}.
                </span>
              </>
            )}
          </div>
        )}
        <h1 className="gameover__title" tabIndex={-1} ref={overHeadingRef}>
          Partita finita
        </h1>
        <p className="gameover__score" data-testid="gameover-score">
          Punteggio: <b>{score}</b>
        </p>
        <p className="gameover__record" data-testid="gameover-best">
          {(serverBest ? serverBest.isNewRecord : bestInfo.isNewRecord) ? (
            <>
              <span className="gameover__newrecord">Nuovo record!</span>{" "}
              <b>{serverBest ? serverBest.bestScore : bestInfo.best}</b>
            </>
          ) : (
            <>
              Record: <b>{serverBest ? serverBest.bestScore : bestInfo.best}</b>
            </>
          )}
        </p>
        {noProfile && (
          <div className="gameover__join" data-testid="gameover-join-cta">
            <p className="gameover__join-text">
              Vuoi comparire nella classifica? Crea un nickname.
            </p>
            <button
              type="button"
              className="btn-ghost gameover__join-btn"
              data-testid="gameover-join-button"
              onClick={() => navigate("/classifica")}
            >
              ENTRA IN CLASSIFICA
            </button>
          </div>
        )}
        {notRecorded && (
          <p className="gameover__notrec" data-testid="gameover-not-recorded">
            Punteggio non inviato alla classifica (connessione assente).
          </p>
        )}
        {lastResult && (
          <div className="gameover__card">
            <p className="gameover__line">
              Ultima prova: <span className="gameover__muted">{lastResult.speech}</span>
            </p>
            <p className="gameover__line" data-testid="gameover-taps">
              Hai toccato <b>{lastResult.taps}</b>, la risposta era <b>{lastResult.required}</b>.
            </p>
            {lastResult.explanation && (
              <p className="gameover__line" data-testid="gameover-explanation">
                Perché: <span className="gameover__muted">{lastResult.explanation}</span>
              </p>
            )}
          </div>
        )}
        <div className="gameover__actions">
          <button
            type="button"
            className="btn-primary"
            data-testid="gameover-replay-button"
            onClick={restart}
          >
            RIGIOCA
          </button>
          <button
            type="button"
            className="btn-ghost"
            data-testid="gameover-home-button"
            onClick={() => navigate("/")}
          >
            HOME
          </button>
        </div>
      </main>
    );
  }

  if (view === "countdown") {
    return (
      <main className="game" data-testid="game-screen">
        <button
          type="button"
          className="game__exit"
          data-testid="game-exit-button"
          onClick={() => navigate("/")}
          aria-label="Esci e torna alla schermata principale"
        >
          <ChevronLeft size={16} strokeWidth={2.5} aria-hidden="true" />
          Esci
        </button>
        <div className="countdown" data-testid="game-countdown" aria-hidden="true">
          <span key={countLabel} className="countdown__num" data-testid="game-countdown-value">
            {countLabel}
          </span>
        </div>
      </main>
    );
  }

  return (
    <main className="game" data-testid="game-screen">
      <button
        type="button"
        className="game__exit"
        data-testid="game-exit-button"
        onClick={() => navigate("/")}
        aria-label="Esci dalla partita e torna alla schermata principale"
      >
        <ChevronLeft size={16} strokeWidth={2.5} aria-hidden="true" />
        Esci
      </button>

      {view === "playing" && score > 0 && (
        <div
          className={`game__streak${milestone ? ` game__streak--m${milestone.tier}` : ""}`}
          data-testid="game-streak"
          aria-hidden="true"
          key={milestone ? `ms-${milestone.key}` : "streak"}
        >
          <Flame size={14} strokeWidth={2.5} aria-hidden="true" />
          <span className="game__streak-num" data-testid="game-streak-value">{score}</span>
          {milestone?.tier === 3 && (
            <span className="game__streak-burst" aria-hidden="true">
              <i className="game__spark game__spark--1" />
              <i className="game__spark game__spark--2" />
              <i className="game__spark game__spark--3" />
              <i className="game__spark game__spark--4" />
            </span>
          )}
        </div>
      )}

      <h1 className="sr-only" tabIndex={-1} ref={headingRef}>
        Partita in corso
      </h1>

      <div
        className="game__surface"
        data-testid="game-surface"
        role="button"
        tabIndex={0}
        aria-label={
          accRef.current
            ? "Superficie di gioco. Tocca per rispondere."
            : trial
              ? `${trial.speech} Tocca la superficie per rispondere.`
              : "Prova"
        }
        onPointerDown={onPointerDown}
        onClick={onSurfaceClick}
        onKeyDown={onKeyDown}
      >
        <SegmentTimer progress={progress} />
        {view === "playing" && ripples.length > 0 && (
          <div className="game__ripples" aria-hidden="true">
            {ripples.map((r) => (
              <span
                key={r.id}
                className="game__ripple"
                style={{ left: `${r.x}px`, top: `${r.y}px` }}
              />
            ))}
          </div>
        )}
        {trial && trial.render ? (
          <div className="game__prompt game__prompt--render" data-testid="game-prompt" aria-hidden="true">
            <span className="game__render-q">{trial.prompt}</span>
            <span className="game__render-row">
              {trial.render.map((seg, i) => (
                <span
                  key={i}
                  className="game__render-item"
                  style={{ color: seg.c, fontSize: seg.s ? `${seg.s}em` : undefined }}
                >
                  {seg.t}
                </span>
              ))}
            </span>
          </div>
        ) : trial && trial.id === "countShownNow" ? (
          (() => {
            // Fairness percettiva: stessa prova, stesso numero di elementi e stessa
            // risposta; si migliora SOLO la leggibilità (spaziatura ampia, glifi più
            // grandi, a capo ordinato) per non far perdere il conto su iPhone.
            const [q, row] = trial.prompt.split("\n");
            const items = (row || "").trim().split(/\s+/);
            return (
              <div className="game__prompt game__prompt--count" data-testid="game-prompt" aria-hidden="true">
                <span className="game__count-q">{q}</span>
                <span className="game__count-row">
                  {items.map((e, i) => (
                    <span className="game__count-item" key={i}>{e}</span>
                  ))}
                </span>
              </div>
            );
          })()
        ) : (
          <p className="game__prompt" data-testid="game-prompt" aria-hidden="true">
            {trial ? trial.prompt : ""}
          </p>
        )}
      </div>

      {view === "result" && (
        <div
          className={`game__flash game__flash--${resultCorrect ? "success" : "error"}`}
          data-testid="game-flash"
          aria-hidden="true"
        >
          <span className="game__flash-text">{resultCorrect ? "CORRETTO" : "SBAGLIATO"}</span>
        </div>
      )}

      <p className="sr-only" aria-live="assertive" data-testid="game-result-announce">
        {announce}
      </p>
    </main>
  );
}
